import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  GridItem,
  Heading,
  HStack,
  Text,
  VStack,
  Badge,
  IconButton,
  UnorderedList,
  Image,
  VisuallyHidden,
  ListItem,
  Link,
  Stack, // ✅ added for responsive layouts
} from "@chakra-ui/react";
import { ArrowForwardIcon } from "@chakra-ui/icons";
import { Link as RouterLink, useLocation } from "react-router-dom";
import PromoBanner from "../components/PromoBanner";

/* ===== Brand colors ===== */
const brand = {
  green: "#1C4C3F",
  cream: "#F7F5EB",
  mint: "#D9EFE6",
  ink: "#0E2721",
};

/* ===== CTA label (fixed) ===== */
const CTA_LABEL = "Get started";

/* ===== Small UI atoms ===== */
const Pill = ({ children }) => (
  <Box
    as="span"
    px={3}
    py={1}
    borderRadius="full"
    bg="whiteAlpha.800"
    border="1px solid rgba(28,76,63,0.15)"
    fontSize="sm"
  >
    {children}
  </Box>
);

function FeatureCard({ title, body, href, cta }) {
  return (
    <Box p={6} bg="#FFFFFF" border="1px solid #EAEAEA" borderRadius="lg" boxShadow="sm">
      <VStack align="start" spacing={3}>
        <Badge bg="#D9EFE6" color="#0E2721" borderRadius="md" px={2} py={1}>
          {title}
        </Badge>
        <Text>{body}</Text>
        {/* Link removed by not passing href in callers below */}
        {href && (
          <Button as={Link} href={href} variant="link" rightIcon={<ArrowForwardIcon />}>
            {cta || "Learn more"}
          </Button>
        )}
      </VStack>
    </Box>
  );
}

/* ===== HERO with rotating agent slides ===== */
function HeroAgents() {
  const slides = useMemo(
    () => [
      {
        role: "Commercial Strategy Director",
        blurb:
          "Focus your limited budget where it moves the needle. Get a crisp 90-day plan: which retailers, which packs, and what good looks like.",
        prompt: "What should be our 6-month growth roadmap?",
        output:
          "Clarify the market, focus your strategy, optimize your product and pricing, execute the plan, and refine based on results.",
        image: "/images/commercial-strategy-director.png",
        alt: "Commercial Strategy Director agent view",
      },
      {
        role: "Trade Promo Analyst",
        blurb:
          "Stop guessing. See promo ROAS, expected lift, and the next best promo based on your past events.",
        prompt: "Is 25% lift considered good for a BOGO promo?",
        output:
          "Historical lift +21% on TPR only; display adds +9%. 25% off erodes margin; recommend TPR + display, 2 weeks, cap at $8.99.",
        image: "/images/trade-promo-analyst.png",
        alt: "Trade Promo Analyst agent view",
      },
      {
        role: "Category Manager",
        blurb:
          "Translate your data into buyer-ready talking points: distribution gaps, velocity benchmarks, and pack architecture.",
        prompt: "What’s the whitespace in the functional hydration category?",
        output:
          "To find whitespace, review market data and trends. Functional hydration has grown ~8% annually over the last three years.",
        image: "/images/category-manager.png",
        alt: "Category Manager agent view"
      },
      {
        role: "Retail Buyer (simulator)",
        blurb:
          "Pressure-test your pitch against a buyer’s objections and get a tighter sell-in narrative.",
        prompt: "Would a Natural Retailer add my 12-oz functional beverage SKU at $5.99?",
        output:
          "As a retail buyer we need to look at Category Fit, Brand Positioning and Margin Potential.",
        image: "/images/retailer-buyer.png",
        alt: "Retail Buyer simulator view"
      },
      {
        role: "Auto Chart Generator",
        blurb:
          "Upload a CSV or Excel file and get clean charts plus slide-ready insights—no formulas, no formatting.",
        prompt: "Bar Chart ACV by Retailer",
        output:
          "ACV by Region, Northeast tops West by (88 vs 83).",
        image: "/images/sample-chart.png",
        alt: "Sample chart generated from ShelfKey",
      },
    ],
    []
  );

  const [idx, setIdx] = useState(0);
  const timerRef = useRef(null);
  const advance = () => setIdx((p) => (p + 1) % slides.length);

  useEffect(() => {
    timerRef.current = setInterval(advance, 6000);
    return () => clearInterval(timerRef.current);
  }, []);

  return (
    <Box bg={brand.green} color="white">
      <Container maxW="6xl" px={{ base: 4, md: 6 }} py={{ base: 14, md: 20 }}>
        <Grid templateColumns={{ base: "1fr", lg: "1.05fr 0.95fr" }} gap={10} alignItems="center">
          <GridItem>
            <VStack align="start" spacing={6}>
              <Heading
                as="h1"
                size="2xl"
                lineHeight={1.1}
                fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}  // ✅ responsive heading
              >
                On-Demand CPG Expertise.
              </Heading>
              <Text fontSize={{ base: "md", md: "lg" }} opacity={0.95}>
                ShelfKey gives you on-demand experts— <strong>Commercial Strategy Director</strong>,{" "}
                <strong>Trade Promo Analyst</strong>, <strong>Category Manager</strong>,{" "}
                <strong>Retail Buyer</strong>, and <strong>Data Analyst</strong>—so you get clear
                answers and buyer-ready recommendations in minutes.
              </Text>

              <Stack
                direction={{ base: "column", sm: "row" }}   // ✅ stack vertically on mobile
                spacing={4}
                align={{ base: "stretch", sm: "center" }}
              >
                <Button
                  as={RouterLink}
                  to="/signup"
                  size="lg"
                  bg="white"
                  color={brand.green}
                  _hover={{ bg: brand.mint }}
                  rightIcon={<ArrowForwardIcon />}
                  width={{ base: "100%", sm: "auto" }}     // ✅ full-width button on mobile
                >
                  {CTA_LABEL}
                </Button>
                {/* Secondary CTA removed per request */}
              </Stack>
            </VStack>
          </GridItem>

          {/* Slide card */}
          <GridItem id="agents">
            <Box
              bg="white"
              color={brand.ink}
              borderRadius="lg"
              p={6}
              boxShadow="xl"
              minH="320px"
            >
              <VStack align="stretch" spacing={4}>
                <HStack justify="space-between" align="start">
                  <Badge bg={brand.mint} color={brand.ink} borderRadius="md" px={2} py={1}>
                    {slides[idx].role}
                  </Badge>
                  <HStack spacing={2}>
                    <IconButton
                      aria-label="Previous"
                      size="sm"
                      onClick={() => setIdx((idx - 1 + slides.length) % slides.length)}
                      children="‹"
                    />
                    <IconButton
                      aria-label="Next"
                      size="sm"
                      onClick={() => setIdx((idx + 1) % slides.length)}
                      children="›"
                    />
                  </HStack>
                </HStack>

                <Text>{slides[idx].blurb}</Text>

                <Box bg={brand.cream} border="1px solid #e7e7e7" borderRadius="md" p={3}>
                  <Text fontSize="sm" opacity={0.7} mb={1}>
                    You ask
                  </Text>
                  <Text fontWeight="semibold">“{slides[idx].prompt}”</Text>
                </Box>

                <Box bg="white" border="1px dashed #e3e3e3" borderRadius="md" p={3}>
                  <Text fontSize="sm" opacity={0.7} mb={1}>
                    Agent reply
                  </Text>
                  <Text>{slides[idx].output}</Text>
                </Box>

                {/* Image slot: now per slide */}
                <Box mt={2}>
                  <Image
                    src={slides[idx].image}
                    alt={slides[idx].alt}
                    borderRadius="6px"
                    border="1px solid #e0e0e0"
                    w="100%"
                    h="auto"
                    loading="eager"
                    decoding="async"
                    htmlWidth="880"
                    htmlHeight="560"
                  />
                </Box>

                {/* Dots */}
                <HStack justify="center" pt={2}>
                  {slides.map((_, i) => (
                    <Box
                      key={i}
                      as="button"
                      aria-label={`Go to slide ${i + 1}`}
                      onClick={() => setIdx(i)}
                      w="10px"
                      h="10px"
                      borderRadius="full"
                      bg={i === idx ? brand.green : "blackAlpha.300"}
                    />
                  ))}
                </HStack>
              </VStack>
            </Box>
          </GridItem>
        </Grid>
      </Container>
    </Box>
  );
}

/* ===== Page ===== */
export default function LandingPage() {
  const location = useLocation();

  useEffect(() => {
    const map = { "/features": "features", "/pricing": "pricing" };
    const id = map[location.pathname];

    // also support /#features or /#pricing if someone links with a hash
    const target = id
      ? document.getElementById(id)
      : location.hash
      ? document.getElementById(location.hash.slice(1))
      : null;

    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [location.pathname, location.hash]);

  return (
    <Box bg={brand.cream} color={brand.ink}>
      <PromoBanner />
      {/* NAV */}
      <Container maxW="6xl" px={{ base: 4, md: 6 }} py={4}>
        <HStack justify="space-between">
          <HStack spacing={3} align="center">
            <Link as={RouterLink} to="/" display="inline-flex" alignItems="center">
              <Image src="/favicon-64x64.png" alt="ShelfKey logo" h="28px" w="28px" loading="eager" />
              <VisuallyHidden>ShelfKey</VisuallyHidden>
            </Link>
            <Heading size="md" color={brand.green}>
              ShelfKey
            </Heading>
          </HStack>

          <HStack spacing={4} display={{ base: "none", md: "flex" }}>
            <Link as={RouterLink} to="/features">
              Features
            </Link>

            {/* NEW: Insights + 'Coming soon' pill */}
            <HStack
              as={RouterLink}
              to="/insights"
              spacing={2}
              _hover={{ textDecoration: "none", opacity: 0.9 }}
            >
              <Text>Insights</Text>
              <Pill>Coming soon</Pill>
            </HStack>

            <Link as={RouterLink} to="/pricing">
              Pricing
            </Link>
            <Link as={RouterLink} to="/login">
              Login
            </Link>
          </HStack>

          <Button
            as={RouterLink}
            to="/signup"
            bg={brand.green}
            color="white"
            _hover={{ opacity: 0.9 }}
            size={{ base: "sm", md: "md" }}  // ✅ slightly smaller on mobile
          >
            {CTA_LABEL}
          </Button>
        </HStack>
      </Container>

      {/* HERO */}
      <HeroAgents />

      {/* PAIN → SOLUTION */}
      <Container maxW="6xl" px={{ base: 4, md: 6 }} py={{ base: 14, md: 20 }}>
        <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={10}>
          <GridItem>
            <Heading size="lg" mb={4}>
              Spend less time in spreadsheets
            </Heading>
            <UnorderedList spacing={3}>
              <ListItem>Wrangling CSVs into presentable charts</ListItem>
              <ListItem>Rebuilding visuals for every buyer deck</ListItem>
              <ListItem>Starting insights from zero before every deck</ListItem>
            </UnorderedList>
          </GridItem>
          <GridItem>
            <Heading size="lg" mb={4}>
              ShelfKey gives you the fast path
            </Heading>
            <UnorderedList spacing={3}>
              <ListItem>
                Upload → choose metric → <strong>chart + insights</strong> in &lt; 2 minutes
              </ListItem>
              <ListItem>Smart defaults for ACV, Velocity, Promo Lift, Distribution</ListItem>
              <ListItem>Drop in image for PowerPoint/Slides</ListItem>
            </UnorderedList>
          </GridItem>
        </Grid>
      </Container>

      {/* FEATURES */}
      <Box id="features" bg="white">
        <Container maxW="6xl" px={{ base: 4, md: 6 }} py={{ base: 12, md: 16 }}>
          <Heading textAlign="center" size="lg" mb={10}>
            What you get
          </Heading>
          <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={6}>
            {/* Links removed by omitting href/cta props */}
            <FeatureCard
              title="AI CPG Advisors"
              body="On-demand experts for strategy, trade, category, buying, and analysis. Get concise, buyer-ready recommendations."
            />
            <FeatureCard
              title="Auto Charting"
              body="Turn raw sales files into clean, on-brand charts: bar, grouped, stacked, line, dual-axis, and pie. Export PNG/PDF."
            />
            <FeatureCard
              title="Pricing Tools"
              body="Quick margin math, promo ROI, and guardrails for your sell-in. Take the guesswork out of price and promo."
            />
          </Grid>
        </Container>
      </Box>

      {/* ===== NEW: WHY AGENTS VS GENERIC AI ===== */}
      <Container maxW="6xl" px={{ base: 4, md: 6 }} pb={{ base: 6, md: 10 }} pt={0}>
        <Box bg="#FFFFFF" border="1px solid #cbe5d9" borderRadius="lg" p={{ base: 5, md: 6 }}>
          <Heading size="md" mb={2}>
            Why agents beat generic AI
          </Heading>
          <Text fontSize="sm" opacity={0.8} mb={3}>
            Built for CPG workflows — not generic chat.
          </Text>
          <UnorderedList spacing={2}>
            <ListItem>
              <Text as="span" fontWeight="semibold">
                Speaks CPG:
              </Text>{" "}
              Uses retail math and terms you use daily (ACV, velocity, TPRs, case packs).
            </ListItem>
            <ListItem>
              <Text as="span" fontWeight="semibold">
                Action-ready outputs:
              </Text>{" "}
              Slide-ready bullets: <em>What happened / Why it matters / What to do next</em>.
            </ListItem>
            <ListItem>
              <Text as="span" fontWeight="semibold">
                Repeatable workflows:
              </Text>{" "}
              Role-based guardrails for pricing, promo caps, and sell-in best practices—no prompt engineering.
            </ListItem>
          </UnorderedList>
        </Box>
      </Container>
      {/* ===== END WHY AGENTS ===== */}

      {/* PRIVACY & CONTROL */}
      <Container maxW="6xl" px={{ base: 4, md: 6 }} py={{ base: 10, md: 12 }}>
        <Box bg="#FFFFFF" border="1px solid #EAEAEA" borderRadius="lg" p={{ base: 5, md: 6 }}>
          <Heading size="md" mb={2}>
            Privacy & control
          </Heading>
          <Text fontSize="sm" opacity={0.8} mb={3}>
            Your data, your rules.
          </Text>
          <UnorderedList spacing={2}>
            <ListItem>
              <Text as="span" fontWeight="semibold">
                Your data stays yours.
              </Text>{" "}
              We don’t use your files or chats to train public AI models.
            </ListItem>
            <ListItem>
              <Text as="span" fontWeight="semibold">
                You decide what’s saved.
              </Text>{" "}
              Default is ephemeral processing.
            </ListItem>
            <ListItem>
              <Text as="span" fontWeight="semibold">
                One-click purge.
              </Text>{" "}
              Delete chat history anytime.
            </ListItem>
            <ListItem>
              <Text as="span" fontWeight="semibold">
                Encrypted.
              </Text>{" "}
              TLS in transit, AES-256 at rest, per-workspace isolation.
            </ListItem>
            <ListItem>
              <Text as="span" fontWeight="semibold">
                No routine human access.
              </Text>{" "}
              Only with your explicit support request.
            </ListItem>
          </UnorderedList>
        </Box>
      </Container>

      {/* CREDIBILITY */}
      <Container maxW="6xl" px={{ base: 4, md: 6 }} py={{ base: 12, md: 16 }}>
        <Grid templateColumns={{ base: "1fr", md: "1.2fr 0.8fr" }} gap={10} alignItems="center">
          <GridItem>
            <Heading size="lg" mb={4}>
              Built by CPG operators.
            </Heading>
            <Text fontSize="md">
              We’ve lived the late-night deck grind. ShelfKey does the repetitive work so you can
              focus on the story and the sell-in. Your files are processed securely—you control
              what’s saved and can delete any dataset with one click.
            </Text>
          </GridItem>
          <GridItem>
            <Box bg={brand.mint} p={5} borderRadius="md" border="1px solid #cbe5d9">
              <Text fontWeight="semibold" mb={2}>
                Why users keep it:
              </Text>
              <UnorderedList spacing={2}>
                <ListItem>On-demand domain expertise</ListItem>
                <ListItem>Cleaner charts with zero formatting</ListItem>
                <ListItem>Confidence in what to say on each slide</ListItem>
              </UnorderedList>
            </Box>
          </GridItem>
        </Grid>
      </Container>

      {/* PRICING */}
      <Box id="pricing" bg="white">
        <Container maxW="6xl" px={{ base: 4, md: 6 }} py={{ base: 12, md: 16 }}>
          <VStack spacing={4}>
            <Heading size="lg">$19.99/month</Heading>
            <Text>Simple, founder-friendly pricing. Cancel anytime.</Text>
            <Stack
              direction={{ base: "column", sm: "row" }}  // ✅ stacked buttons on mobile
              spacing={4}
              justify="center"
              align={{ base: "stretch", sm: "center" }}
            >
              <Button
                as="a"
                href="/signup"
                bg={brand.green}
                color="white"
                _hover={{ opacity: 0.9 }}
                width={{ base: "100%", sm: "auto" }}     // ✅ full-width on mobile
              >
                {CTA_LABEL}
              </Button>
              <Button
                as="a"
                href="/contact"
                variant="outline"
                width={{ base: "100%", sm: "auto" }}
              >
                Talk to us
              </Button>
            </Stack>
          </VStack>
        </Container>
      </Box>

      {/* FOOTER */}
      <Box bg={brand.green} color="white">
        <Container maxW="6xl" px={{ base: 4, md: 6 }} py={10}>
          <VStack spacing={4} textAlign="center">
            <Heading size="md">Ready to turn your data into decisions?</Heading>
            <Button
              as="a"
              href="/signup"
              bg="white"
              color={brand.green}
              _hover={{ bg: brand.mint }}
              width={{ base: "100%", sm: "auto" }}   // ✅ nicer on mobile
            >
              {CTA_LABEL}
            </Button>
            <Text opacity={0.8}>
              Questions?{" "}
              <Link href="mailto:hello@shelfkey.co" color="white">
                hello@shelfkey.co
              </Link>
            </Text>

            {/* NEW: legal links */}
            <HStack spacing={4} justify="center" pt={2} opacity={0.9}>
              <Link as={RouterLink} to="/terms" color="white" textDecoration="underline">
                Terms &amp; Conditions
              </Link>
              <Text>•</Text>
              <Link as={RouterLink} to="/privacy" color="white" textDecoration="underline">
                Privacy Policy
              </Link>
            </HStack>
          </VStack>
        </Container>
      </Box>
    </Box>
  );
}
